// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Turning per-frame scores into moments a person can act on.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: rank, never threshold.
 *
 * MobileCLIP's similarity scores are uncalibrated. There is no number above
 * which a match is real. Measured on this model over eight photographs and
 * eight queries, a correct best match scored anywhere from 0.105 to 0.244 —
 * so 0.15 is an excellent hit for one query and worse than nothing for
 * another. Any tool that ships a fixed cut-off is silently right on some
 * queries and silently empty on others, and the user cannot tell which.
 *
 * So: always return the best N moments, always show them with real thumbnails,
 * and let the person move the cut-off while looking at frames. The only place a
 * data-derived level appears is in deciding how long a moment LASTS, and even
 * there it is the median of this video's own scores — "more like your query
 * than a typical frame of this footage is" — never a constant.
 */

import type { Moment, MomentSettings } from '../types.ts';

/**
 * Temporal median filter over the score series.
 *
 * A single frame can score high for the wrong reason — a headlight flare, a
 * compression artefact, one blurred frame that happens to look like anything.
 * The median is used rather than a mean precisely because it discards those
 * outright instead of smearing them across their neighbours.
 *
 * An even window would have no true middle element, so it is rounded up to odd.
 * A window of 1 returns the input untouched, which is a real setting: on
 * sparsely sampled footage the neighbours are seconds apart and smoothing
 * across them destroys more than it fixes.
 */
export function medianFilter(scores: Float32Array | readonly number[], window: number): Float32Array {
  const n = scores.length;
  const out = new Float32Array(n);
  const w = Math.max(1, Math.floor(window) | 1);
  if (w === 1 || n === 0) {
    for (let i = 0; i < n; i++) out[i] = scores[i]!;
    return out;
  }
  const half = (w - 1) / 2;
  const buf: number[] = [];
  for (let i = 0; i < n; i++) {
    buf.length = 0;
    // Clamped at the edges rather than zero-padded: padding with zero would
    // drag the first and last moments of every video downward, and the start
    // of a dashcam file is exactly where an incident often is.
    for (let k = -half; k <= half; k++) {
      const j = Math.min(n - 1, Math.max(0, i + k));
      buf.push(scores[j]!);
    }
    buf.sort((a, b) => a - b);
    out[i] = buf[half]!;
  }
  return out;
}

export function median(values: Float32Array | readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = Array.from(values).sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * How far the best frame stands out from a typical frame of THIS video.
 *
 * The number the interface needs is not the raw score — cosines are
 * uncalibrated and cannot be compared between queries — but a measure of
 * separation that can. This is a robust z-score: the distance from the median,
 * in units of the median absolute deviation.
 *
 * MAD rather than standard deviation, and median rather than mean, because the
 * thing being measured IS an outlier. A handful of genuinely matching frames
 * would inflate a standard deviation and hide themselves; the median and the
 * MAD are unmoved by them.
 *
 * For calibration: an independent measurement over 262 frames of real 4K
 * forward dashcam footage with this model put true positives in the range
 * z = 1.27 to 3.32. Below about 1.5 there is nothing worth reporting as a
 * find — which is a statement about separation, never about whether the thing
 * is in the video.
 */
export function standout(scores: Float32Array | readonly number[], value: number): number {
  if (scores.length === 0) return 0;
  const mid = median(scores);
  const deviations = Array.from(scores, (s) => Math.abs(s - mid));
  const mad = median(deviations);
  if (mad > 1e-9) return (value - mid) / mad;

  /*
   * MAD is ZERO whenever more than half the frames score identically, which is
   * not a rare edge case — it is what a locked-off camera looking at an empty
   * driveway produces. Returning 0 there would report "nothing stands out" at
   * exactly the moment when one frame stands out most, which is backwards and
   * is a bug this suite caught.
   *
   * So fall back to the standard deviation. It is the less robust statistic and
   * a cluster of genuine matches does inflate it — but inflating the yardstick
   * only ever makes this number SMALLER, so the fallback can understate a find
   * and never invent one. That is the right direction to be wrong in.
   */
  let sum = 0;
  let n = 0;
  for (const s of scores) {
    sum += s;
    n += 1;
  }
  const mean = sum / n;
  let variance = 0;
  for (const s of scores) variance += (s - mean) * (s - mean);
  const sd = Math.sqrt(variance / n);
  // Genuinely every frame identical: there is no scale at all, so there is no
  // separation to report rather than an infinite one.
  if (!(sd > 1e-9)) return 0;
  return (value - mid) / sd;
}

/** The p-th percentile of a score series, p in 0..1. */
function percentile(values: Float32Array | readonly number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = Array.from(values).sort((a, b) => a - b);
  const at = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * p)));
  return sorted[at]!;
}

/**
 * A moment may not run longer than this, in seconds.
 *
 * A guard rather than a tuning knob. The extent walk below stops when the
 * footage drops back to background, which is the right rule — but on a video
 * where almost every frame is slightly above background it would otherwise
 * return the entire recording as one "moment", which is useless as a clip.
 */
const MAX_MOMENT_SECONDS = 120;

/** Below this, the top result is not distinguishable from an ordinary frame. */
export const STANDOUT_FLOOR = 1.5;

/**
 * Collapse a smoothed score series into ranked, non-overlapping moments.
 *
 * Greedy non-maximum suppression: take the best remaining frame, claim the
 * footage around it, suppress everything within `mergeGap` seconds, repeat.
 * That is what stops one long event — a car approaching, passing and leaving —
 * from being returned as eleven separate results.
 *
 * @param scores Smoothed score per sampled frame.
 * @param times  Seconds into the video per sampled frame, ascending.
 */
export function findMoments(
  scores: Float32Array | readonly number[],
  times: readonly number[],
  settings: MomentSettings,
  duration: number,
): Moment[] {
  const n = Math.min(scores.length, times.length);
  if (n === 0) return [];

  /*
   * Background level: the 25th percentile, NOT the median.
   *
   * The extent walk below asks "is this frame still part of the event", which
   * needs an estimate of what the video looks like when the event is NOT
   * happening. The median is the wrong answer whenever the event covers more
   * than half the footage — a query matching most of a video puts the median
   * INSIDE the event, the walk cannot extend past its own peak, and one long
   * event comes back fragmented into a handful of overlapping results. That is
   * a real failure this suite caught, and p25 survives an event covering up to
   * three quarters of the recording.
   */
  const window = scores instanceof Float32Array ? scores.subarray(0, n) : scores.slice(0, n);
  const baseline = percentile(window, 0.25);
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => scores[b]! - scores[a]!);

  const claimed = new Uint8Array(n);
  const moments: Moment[] = [];
  const gap = Math.max(0, settings.mergeGap);

  for (const peak of order) {
    if (moments.length >= Math.max(1, settings.topN)) break;
    if (claimed[peak]) continue;
    const peakTime = times[peak]!;

    // How long the moment lasts: walk outward from the peak for as long as the
    // footage stays above background. The bound is MAX_MOMENT_SECONDS, a
    // runaway guard — deliberately NOT the merge gap, which is about how far
    // apart two results must be and has nothing to say about how long one event
    // lasts. Bounding the walk by the merge gap is what fragmented a single
    // eleven-second event into four results.
    let lo = peak;
    while (lo > 0 && scores[lo - 1]! > baseline && peakTime - times[lo - 1]! <= MAX_MOMENT_SECONDS) lo -= 1;
    let hi = peak;
    while (hi < n - 1 && scores[hi + 1]! > baseline && times[hi + 1]! - peakTime <= MAX_MOMENT_SECONDS) hi += 1;

    for (let i = lo; i <= hi; i++) claimed[i] = 1;
    // Suppress the neighbourhood of the whole claimed span, so the next result
    // is a different event rather than the same one one frame over.
    for (let i = 0; i < n; i++) {
      if (times[i]! >= times[lo]! - gap && times[i]! <= times[hi]! + gap) claimed[i] = 1;
    }

    let start = times[lo]!;
    let end = times[hi]!;
    // A single sampled frame has zero extent, so a moment would be a point.
    // Grow it symmetrically to the minimum the user asked for — an exported
    // clip has to be watchable, and half a second is not.
    const min = Math.max(0, settings.minDuration);
    if (end - start < min) {
      const pad = (min - (end - start)) / 2;
      start -= pad;
      end += pad;
    }
    start = Math.max(0, start);
    end = duration > 0 ? Math.min(duration, end) : end;
    if (!(end > start)) end = start + Math.max(min, 0.5);

    moments.push({
      start: round(start),
      end: round(end),
      peakTime: round(peakTime),
      peakScore: scores[peak]!,
      peakIndex: peak,
    });
  }

  // Ranked by score for the list, but overlapping ranges can still survive the
  // suppression when minDuration pads two nearby moments into each other, so
  // merge them before anything is exported. Two clips covering the same
  // seconds is a bug the user sees in their download folder.
  return mergeOverlaps(moments, gap);
}

function mergeOverlaps(moments: readonly Moment[], gap: number): Moment[] {
  const byTime = [...moments].sort((a, b) => a.start - b.start);
  const merged: Moment[] = [];
  for (const m of byTime) {
    const last = merged[merged.length - 1];
    // Merged when they overlap OR when they are closer together than the merge
    // gap, which is exactly what that setting means to the user.
    if (last && m.start <= last.end + gap) {
      merged[merged.length - 1] = {
        start: last.start,
        end: Math.max(last.end, m.end),
        peakTime: last.peakScore >= m.peakScore ? last.peakTime : m.peakTime,
        peakScore: Math.max(last.peakScore, m.peakScore),
        peakIndex: last.peakScore >= m.peakScore ? last.peakIndex : m.peakIndex,
      };
    } else {
      merged.push(m);
    }
  }
  return merged.sort((a, b) => b.peakScore - a.peakScore);
}

const round = (t: number): number => Math.round(t * 1000) / 1000;
